import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { quotationAPI, proposalAPI, pdfAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Clock,
  MapPin,
  Star,
  Package,
  PaperPlaneTilt,
  CircleNotch,
  WarningCircle,
  CheckCircle,
  FilePdf,
  DownloadSimple,
  ChatCircle,
  CurrencyDollar,
  ShieldCheck,
  Handshake,
  FileText,
  Trophy,
  Phone,
  Envelope,
  Buildings,
  FloppyDisk,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import MatchitoCelebration from '../components/MatchitoCelebration';
import DeliveryMap from '../components/DeliveryMap';
import MaterialImageUpload from '../components/MaterialImageUpload';
import ContactInfoCard from '../components/ContactInfoCard';
import RatingCard from '../components/RatingCard';

const QuotationDetailPage = () => {
  const { quotationId } = useParams();
  const navigate = useNavigate();
  const { user, activeRole, isClient } = useAuth();
  const [quotation, setQuotation] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [proposalItems, setProposalItems] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [hasExistingProposal, setHasExistingProposal] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [confirmingPurchase, setConfirmingPurchase] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [showMatchito, setShowMatchito] = useState(false);
  const [matchitoMsg, setMatchitoMsg] = useState('');
  const [providerDistance, setProviderDistance] = useState(null);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Haversine distance calculation (km)
  const calcDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const fetchQuotationData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await quotationAPI.getById(quotationId);
      setQuotation(response.data);

      const items = (response.data.materials || []).map(m => ({
        material_name: m.name,
        quantity: m.quantity,
        unit: m.unit,
        price: '',
        brand: '',
        available: true,
        shipping_cost: 0,
        delivery_time: '',
        note: '',
      }));
      setProposalItems(items);

      const isOwnerOfQuotation = user?.user_id === response.data.client_id;
      const isAdminUser = user?.role === 'admin';

      // Check if provider already sent proposal OR load draft
      if (!isOwnerOfQuotation && (activeRole === 'proveedor' || user?.role === 'proveedor' || user?.role === 'ambos')) {
        try {
          const myProposals = await proposalAPI.getMine();
          const existing = myProposals.data.find(p => p.quotation_id === quotationId);
          if (existing) {
            setHasExistingProposal(true);
          } else {
            // Try to load draft
            try {
              const draftRes = await proposalAPI.getDraft(quotationId);
              if (draftRes.data?.items?.length > 0) {
                const draftItems = draftRes.data.items.map((di, idx) => ({
                  material_name: di.material_name || items[idx]?.material_name || '',
                  quantity: di.quantity || items[idx]?.quantity || 1,
                  unit: di.unit || items[idx]?.unit || 'pza',
                  price: di.price || '',
                  brand: di.brand || '',
                  available: di.available !== undefined ? di.available : true,
                  shipping_cost: di.shipping_cost || 0,
                  delivery_time: di.delivery_time || '',
                  note: di.note || '',
                }));
                setProposalItems(draftItems);
                setDiscount(draftRes.data.discount_percent || 0);
                setDraftLoaded(true);
                setLastSavedAt(draftRes.data.updated_at);
              }
            } catch (e) { /* no draft exists, ok */ }
          }
        } catch (e) { /* ok */ }
      }

      // Only fetch proposals if user is the client owner or admin
      if (isOwnerOfQuotation || isAdminUser) {
        try {
          const proposalsRes = await quotationAPI.getProposals(quotationId);
          setProposals(proposalsRes.data || []);
        } catch (e) { /* ok */ }
      }

      // Fetch payment status if winner exists
      if (response.data.winner_proposal_id) {
        try {
          const ps = await quotationAPI.getPaymentStatus(quotationId);
          setPaymentStatus(ps.data);
          if (ps.data.chat_available) {
            try {
              const ci = await import('../lib/api').then(m => m.chatAPI.getContactInfo(quotationId));
              setContactInfo(ci.data);
            } catch (e) { /* ok */ }
          }
        } catch (e) { /* ok */ }
      }
    } catch (error) {
      console.error('Failed to fetch quotation:', error);
      toast.error('Error al cargar la cotización');
    } finally {
      setLoading(false);
    }
  }, [quotationId, activeRole, user, isClient]);

  useEffect(() => { fetchQuotationData(); }, [fetchQuotationData]);

  // Calculate provider distance to delivery location
  useEffect(() => {
    if (!quotation?.delivery_location?.latitude || isClient) return;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const dist = calcDistance(
            pos.coords.latitude, pos.coords.longitude,
            quotation.delivery_location.latitude, quotation.delivery_location.longitude
          );
          setProviderDistance(Math.round(dist * 10) / 10);
        },
        () => {}
      );
    }
  }, [quotation, isClient]);

  const handleItemChange = (index, field, value) => {
    setProposalItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const calculateTotals = () => {
    let materialsSubtotal = 0;
    let totalShipping = 0;
    proposalItems.forEach(item => {
      if (item.available && item.price) {
        materialsSubtotal += parseFloat(item.price) * (item.quantity || 1);
        totalShipping += parseFloat(item.shipping_cost || 0);
      }
    });
    const subtotal = materialsSubtotal + totalShipping;
    const discountAmount = subtotal * (discount / 100);
    const subtotalAfterDiscount = subtotal - discountAmount;
    const iva = subtotalAfterDiscount * 0.16;
    const total = subtotalAfterDiscount + iva;
    return { materialsSubtotal, totalShipping, subtotal, discountAmount, iva, total };
  };

  const handleSubmitProposal = async () => {
    const hasAvailableItems = proposalItems.some(item => item.available && parseFloat(item.price) > 0);
    if (!hasAvailableItems) { toast.error('Debes tener al menos un material con precio'); return; }
    setSubmitting(true);
    try {
      await proposalAPI.create({
        quotation_id: quotationId,
        items: proposalItems.map(item => ({
          material_name: item.material_name,
          price: parseFloat(item.price) || 0,
          brand: item.brand || null,
          available: item.available,
          shipping_cost: parseFloat(item.shipping_cost) || 0,
          delivery_time: item.delivery_time || null,
          note: item.note || null,
        })),
        iva_percent: 16,
        discount_percent: parseFloat(discount) || 0,
      });
      toast.success('Propuesta enviada exitosamente');
      setMatchitoMsg('¡Propuesta enviada exitosamente!');
      setShowMatchito(true);
      setTimeout(() => navigate('/proposals'), 4000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar la propuesta');
    } finally { setSubmitting(false); }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const draftData = {
        quotation_id: quotationId,
        items: proposalItems.map(item => ({
          material_name: item.material_name,
          quantity: item.quantity,
          unit: item.unit,
          price: item.price,
          brand: item.brand || '',
          available: item.available,
          shipping_cost: item.shipping_cost || 0,
          delivery_time: item.delivery_time || '',
          note: item.note || '',
        })),
        discount_percent: parseFloat(discount) || 0,
      };
      await proposalAPI.saveDraft(draftData);
      setDraftLoaded(true);
      setLastSavedAt(new Date().toISOString());
      toast.success('Borrador guardado exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar borrador');
    } finally { setSavingDraft(false); }
  };

  const handleSelectWinner = async (proposalId) => {
    try {
      await quotationAPI.selectWinner(quotationId, proposalId);
      setMatchitoMsg('¡Ganador seleccionado!');
      setShowMatchito(true);
      fetchQuotationData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al seleccionar ganador');
    }
  };

  const handleConfirmPurchase = async () => {
    setConfirmingPurchase(true);
    try {
      await quotationAPI.payCommission(quotationId);
      setMatchitoMsg('¡Compra confirmada exitosamente!');
      setShowMatchito(true);
      setShowConfirmDialog(false);
      fetchQuotationData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al confirmar la compra');
    } finally { setConfirmingPurchase(false); }
  };

  const handleDownloadQuotationPDF = async () => {
    setDownloadingPDF(true);
    try {
      const response = await pdfAPI.downloadQuotationPDF(quotationId);
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `cotizacion_${quotation.key || quotationId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (error) { toast.error('Error al descargar el PDF'); }
    finally { setDownloadingPDF(false); }
  };

  const handleDownloadProposalPDF = async (proposalId) => {
    try {
      const response = await pdfAPI.downloadProposalPDF(proposalId);
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `propuesta_${proposalId.slice(0, 12)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (error) { toast.error('Error al descargar el PDF'); }
  };

  const handleDownloadContractPDF = async () => {
    try {
      const response = await quotationAPI.downloadContractPDF(quotationId);
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `contrato_${quotation.key || quotationId}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Contrato descargado');
    } catch (error) { toast.error('Error al descargar el contrato'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }
  if (!quotation) {
    return (
      <div className="text-center py-20">
        <WarningCircle className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Cotización no encontrada</h2>
        <Link to="/quotations" className="text-orange-600 hover:underline mt-2 inline-block">Volver</Link>
      </div>
    );
  }

  const isOwner = user?.user_id === quotation.client_id;
  const isProviderView = !isOwner && (activeRole === 'proveedor' || user?.role === 'proveedor' || user?.role === 'ambos');
  const totals = calculateTotals();
  const ps = paymentStatus;

  return (
    <div className="space-y-6" data-testid="quotation-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="rounded-none" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{quotation.name}</h1>
          <div className="flex items-center gap-4 mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            <Badge variant="outline" className="rounded-none bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300">{quotation.category}</Badge>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" />Límite: {new Date(quotation.deadline).toLocaleDateString('es-MX')}</span>
          </div>
        </div>
        <Button variant="outline" className="rounded-none" onClick={handleDownloadQuotationPDF} disabled={downloadingPDF} data-testid="download-quotation-pdf">
          {downloadingPDF ? <CircleNotch className="w-4 h-4 animate-spin" /> : <><FilePdf className="w-4 h-4 mr-2" />PDF</>}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Payment Flow Status Card (for client and admin) */}
          {(isOwner || user?.role === 'admin') && ps && ps.has_winner && (
            <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="payment-flow-card">
              <CardHeader className="border-b border-zinc-200 dark:border-zinc-700">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Handshake className="w-5 h-5 text-orange-500" />
                  Flujo de Compra-Venta
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {/* Progress Steps */}
                <div className="flex items-center justify-between mb-6">
                  {[
                    { label: 'Ganador', done: true },
                    { label: 'Confirmar', done: ps.client_confirmed },
                    { label: 'Aceptar', done: ps.provider_accepted },
                    { label: 'Comisión', done: ps.provider_paid },
                    { label: 'Chat', done: ps.chat_available },
                  ].map((step, i, arr) => (
                    <div key={step.label} className="flex items-center flex-1">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          step.done ? 'bg-green-500 text-white' : 'bg-zinc-200 dark:bg-zinc-700 text-zinc-500'
                        }`}>
                          {step.done ? <CheckCircle className="w-5 h-5" /> : i + 1}
                        </div>
                        <span className="text-xs mt-1 text-zinc-500 dark:text-zinc-400">{step.label}</span>
                      </div>
                      {i < arr.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-1 ${step.done ? 'bg-green-400' : 'bg-zinc-200 dark:bg-zinc-700'}`} />
                      )}
                    </div>
                  ))}
                </div>

                {/* Action based on current step */}
                {ps.current_step === 'awaiting_client_confirmation' && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
                    <h4 className="font-semibold text-amber-800 dark:text-amber-300 mb-2">Confirma tu Compra</h4>
                    <p className="text-sm text-amber-700 dark:text-amber-400 mb-1">
                      Has seleccionado un ganador. Confirma tu compra para continuar el proceso.
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-500 mb-4">
                      La confirmación es <strong>sin costo</strong> para el cliente (0% comisión).
                    </p>
                    <Button className="btn-primary rounded-none" onClick={() => setShowConfirmDialog(true)} data-testid="confirm-purchase-btn">
                      <ShieldCheck className="w-5 h-5 mr-2" />
                      Confirmar Compra
                    </Button>
                  </div>
                )}

                {ps.current_step === 'awaiting_provider_acceptance' && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CircleNotch className="w-5 h-5 text-blue-500 animate-spin" />
                      <h4 className="font-semibold text-blue-800 dark:text-blue-300">Esperando al Proveedor</h4>
                    </div>
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                      Ya confirmaste tu compra. Ahora el proveedor debe aceptar la venta.
                    </p>
                  </div>
                )}

                {ps.current_step === 'awaiting_provider_payment' && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CircleNotch className="w-5 h-5 text-blue-500 animate-spin" />
                      <h4 className="font-semibold text-blue-800 dark:text-blue-300">Proveedor Aceptó — Esperando Pago</h4>
                    </div>
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                      El proveedor aceptó la venta. Está pendiente el pago de la comisión del 3%.
                    </p>
                  </div>
                )}

                {ps.current_step === 'completed' && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-green-800 dark:text-green-300">Transacción Completada</h4>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-400 mb-3">
                      El chat y la información de contacto están disponibles.
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Link to={`/chat/${quotationId}`}>
                        <Button variant="outline" className="rounded-none border-green-300 text-green-700" data-testid="go-to-chat-btn">
                          <ChatCircle className="w-4 h-4 mr-2" />Chat
                        </Button>
                      </Link>
                      <Button variant="outline" className="rounded-none border-orange-300 text-orange-700" onClick={handleDownloadContractPDF} data-testid="download-contract-btn">
                        <FileText className="w-4 h-4 mr-2" />Descargar Contrato
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Contact Info Card (after completion) */}
          {(isOwner || user?.role === 'admin') && contactInfo && ps?.chat_available && (
            <ContactInfoCard
              title="Información del Proveedor"
              data={contactInfo.provider}
              role="provider"
            />
          )}

          {/* Rating Card — appears once the contract is active and persists after closure so both parties can still rate */}
          {isOwner && ps?.client_confirmed && ps?.provider_paid && (
            <RatingCard
              quotationId={quotation.quotation_id}
              role="cliente"
              counterpartName={contactInfo?.provider?.business_name || contactInfo?.provider?.name}
            />
          )}

          {/* Materials List */}
          <Card className="border-zinc-200 dark:border-zinc-700 rounded-none">
            <CardHeader className="border-b border-zinc-200 dark:border-zinc-700">
              <CardTitle className="text-base">Materiales ({quotation.materials?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full data-table">
                <thead><tr><th className="w-16"></th><th>Material</th><th className="text-center">Cantidad</th><th className="text-center">Unidad</th></tr></thead>
                <tbody>
                  {quotation.materials?.map((material, index) => (
                    <tr key={index}>
                      <td className="p-2">
                        <MaterialImageUpload imageUrl={material.image_url} readOnly={true} size="sm" />
                      </td>
                      <td className="font-medium">{material.name}</td>
                      <td className="text-center">{material.quantity}</td>
                      <td className="text-center">{material.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Provider: Proposal Form */}
          {isProviderView && !hasExistingProposal && quotation.status === 'sent' && (
            <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="proposal-form">
              <CardHeader className="border-b border-zinc-200 dark:border-zinc-700">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base">Enviar Propuesta</CardTitle>
                  {/* Progress Indicator */}
                  <div className="flex items-center gap-3">
                    {draftLoaded && (
                      <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-none text-xs" data-testid="draft-badge">
                        <FloppyDisk className="w-3 h-3 mr-1" />
                        Borrador cargado
                      </Badge>
                    )}
                    <span className="text-xs text-zinc-500" data-testid="progress-indicator">
                      {(() => {
                        const filled = proposalItems.filter(i => i.available && i.price && parseFloat(i.price) > 0).length;
                        const total = proposalItems.length;
                        return `${filled}/${total} materiales con precio`;
                      })()}
                    </span>
                  </div>
                </div>
                {/* Progress Bar */}
                {(() => {
                  const filled = proposalItems.filter(i => i.available && i.price && parseFloat(i.price) > 0).length;
                  const total = proposalItems.length;
                  const pct = total > 0 ? (filled / total * 100) : 0;
                  return (
                    <div className="mt-2 w-full bg-zinc-200 dark:bg-zinc-700 h-1.5 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-300 ${pct === 100 ? 'bg-green-500' : 'bg-orange-400'}`} style={{ width: `${pct}%` }} />
                    </div>
                  );
                })()}
              </CardHeader>
              <CardContent className="p-4 sm:p-6 space-y-4">
                {lastSavedAt && (
                  <div className="text-xs text-zinc-400 flex items-center gap-1" data-testid="last-saved">
                    <FloppyDisk className="w-3 h-3" />
                    Último guardado: {new Date(lastSavedAt).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                  </div>
                )}
                {proposalItems.map((item, index) => (
                  <div key={index} className={`border p-4 space-y-3 ${item.available && item.price && parseFloat(item.price) > 0 ? 'border-green-200 dark:border-green-800 bg-green-50/30 dark:bg-green-900/10' : 'border-zinc-200 dark:border-zinc-700'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Checkbox checked={item.available} onCheckedChange={(checked) => handleItemChange(index, 'available', checked)} />
                        {quotation.materials?.[index]?.image_url && (
                          <MaterialImageUpload imageUrl={quotation.materials[index].image_url} readOnly={true} size="sm" />
                        )}
                        <span className="font-medium text-sm">{item.material_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.available && item.price && parseFloat(item.price) > 0 && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                        <span className="text-xs text-zinc-500">{item.quantity} {item.unit}</span>
                      </div>
                    </div>
                    {item.available && (
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">Precio Unitario *</Label>
                          <Input type="number" min="0" step="0.01" value={item.price} onChange={(e) => handleItemChange(index, 'price', e.target.value)} className="rounded-none" placeholder="0.00" data-testid={`price-input-${index}`} />
                        </div>
                        <div><Label className="text-xs">Marca</Label>
                          <Input value={item.brand} onChange={(e) => handleItemChange(index, 'brand', e.target.value)} className="rounded-none" placeholder="Marca" />
                        </div>
                        <div><Label className="text-xs text-orange-600 font-semibold">Precio de Flete / Envío</Label>
                          <Input type="number" min="0" step="0.01" value={item.shipping_cost} onChange={(e) => handleItemChange(index, 'shipping_cost', e.target.value)} className="rounded-none border-orange-200 focus:border-orange-400" placeholder="0.00" data-testid={`shipping-cost-${index}`} />
                        </div>
                        <div><Label className="text-xs">Tiempo de Entrega</Label>
                          <Input value={item.delivery_time} onChange={(e) => handleItemChange(index, 'delivery_time', e.target.value)} className="rounded-none" placeholder="Ej: 3-5 días" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div><Label>Descuento General (%)</Label>
                  <Input type="number" min="0" max="100" value={discount} onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)} className="rounded-none w-32" />
                </div>
                <Separator />
                <div className="space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-zinc-600">Subtotal Materiales:</span><span>${totals.materialsSubtotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span></div>
                  {totals.totalShipping > 0 && (
                    <div className="flex justify-between text-sm"><span className="text-orange-600">Flete / Envío:</span><span className="text-orange-600">${totals.totalShipping.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span></div>
                  )}
                  {totals.discountAmount > 0 && (
                    <div className="flex justify-between text-sm text-green-600"><span>Descuento ({discount}%):</span><span>-${totals.discountAmount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span></div>
                  )}
                  <div className="flex justify-between text-sm"><span className="text-zinc-600">IVA (16%):</span><span>${totals.iva.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span></div>
                  <Separator />
                  <div className="flex justify-between text-lg font-bold"><span>Total:</span><span className="text-amber-600">${totals.total.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span></div>
                </div>
                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={handleSaveDraft}
                    disabled={savingDraft}
                    variant="outline"
                    className="flex-1 rounded-none h-12 border-2 border-blue-400 text-blue-700 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-300 dark:hover:bg-blue-900/20 font-semibold text-base"
                    data-testid="save-draft-btn"
                  >
                    {savingDraft ? <CircleNotch className="w-5 h-5 animate-spin" /> : <><FloppyDisk className="w-5 h-5 mr-2" />Guardar Borrador</>}
                  </Button>
                  <Button
                    onClick={handleSubmitProposal}
                    disabled={submitting}
                    className="flex-1 btn-primary rounded-none h-12 font-semibold text-base"
                    data-testid="submit-proposal-btn"
                  >
                    {submitting ? <CircleNotch className="w-5 h-5 animate-spin" /> : <><PaperPlaneTilt className="w-5 h-5 mr-2" />Enviar Propuesta</>}
                  </Button>
                </div>
                <p className="text-xs text-center text-zinc-400">
                  Guarda tu progreso con "Guardar Borrador" y continúa después. "Enviar Propuesta" envía la cotización final al cliente.
                </p>
              </CardContent>
            </Card>
          )}

          {hasExistingProposal && isProviderView && (
            <Card className="border-green-200 bg-green-50 dark:bg-green-900/20 rounded-none">
              <CardContent className="p-6 flex items-center gap-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div><h3 className="font-semibold text-green-800 dark:text-green-300">Ya enviaste una propuesta</h3>
                  <p className="text-sm text-green-700 dark:text-green-400">Revisa el estado en tu lista de propuestas</p></div>
                <Link to="/proposals" className="ml-auto"><Button variant="outline" className="rounded-none border-green-300">Ver Mis Propuestas</Button></Link>
              </CardContent>
            </Card>
          )}

          {/* Proposals List for Client - Anonymous View */}
          {(isOwner || user?.role === 'admin') && proposals.length > 0 && (
            <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="proposals-list">
              <CardHeader className="border-b border-zinc-200 dark:border-zinc-700">
                <CardTitle className="text-base">Propuestas Recibidas ({proposals.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0 divide-y divide-zinc-200 dark:divide-zinc-700">
                {proposals.map((proposal, idx) => (
                  <div key={proposal.proposal_id} className="p-4 sm:p-5 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors" data-testid={`proposal-card-${proposal.proposal_id}`}>
                    {/* Header: Anonymous Provider + Rating */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center text-zinc-500 dark:text-zinc-400 font-bold text-sm">
                              {idx + 1}
                            </div>
                            <span className="font-semibold text-zinc-900 dark:text-zinc-100">Anónimo</span>
                          </div>
                          <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                            <Star className="w-3.5 h-3.5 text-amber-500" weight="fill" />
                            <span className="text-sm font-medium text-amber-700 dark:text-amber-300">{proposal.provider_rating?.toFixed(1) || '5.0'}</span>
                            <span className="text-xs text-amber-600 dark:text-amber-400">({proposal.provider_rating_count || 0})</span>
                          </div>
                          {quotation.winner_proposal_id === proposal.proposal_id && (
                            <Badge className="bg-green-500 rounded-none text-white"><Trophy className="w-3 h-3 mr-1" />Ganador</Badge>
                          )}
                        </div>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                          Enviada: {new Date(proposal.created_at).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>

                    {/* Budget Breakdown */}
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                      <div className="bg-zinc-50 dark:bg-zinc-800 p-2">
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">Subtotal</p>
                        <p className="font-semibold text-zinc-900 dark:text-zinc-100">${proposal.subtotal?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="bg-zinc-50 dark:bg-zinc-800 p-2">
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">Descuento</p>
                        <p className="font-semibold text-green-600">{proposal.discount_amount > 0 ? `-$${proposal.discount_amount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '$0.00'}</p>
                      </div>
                      <div className="bg-zinc-50 dark:bg-zinc-800 p-2">
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">IVA (16%)</p>
                        <p className="font-semibold text-zinc-900 dark:text-zinc-100">${proposal.iva_amount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 p-2">
                        <p className="text-xs text-orange-600 dark:text-orange-400">Total</p>
                        <p className="font-bold text-orange-700 dark:text-orange-300">${proposal.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>

                    {proposal.total_shipping > 0 && (
                      <p className="text-xs text-orange-600 mt-1.5">Incluye flete: ${proposal.total_shipping?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3">
                      <Button variant="outline" size="sm" className="rounded-none text-sm" onClick={() => setSelectedProposal(selectedProposal?.proposal_id === proposal.proposal_id ? null : proposal)} data-testid={`view-detail-${proposal.proposal_id}`}>
                        <FileText className="w-4 h-4 mr-1" />
                        {selectedProposal?.proposal_id === proposal.proposal_id ? 'Ocultar Detalle' : 'Ver Detalle'}
                      </Button>
                      {!quotation.winner_proposal_id && (
                        <Button onClick={() => handleSelectWinner(proposal.proposal_id)} className="btn-primary rounded-none text-sm" data-testid={`select-winner-${proposal.proposal_id}`}>
                          <Trophy className="w-4 h-4 mr-1" />
                          Seleccionar como Ganador
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="rounded-none text-orange-600 ml-auto" onClick={() => handleDownloadProposalPDF(proposal.proposal_id)} data-testid={`download-proposal-pdf-${proposal.proposal_id}`}>
                        <FilePdf className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Expanded Detail View - No individual prices */}
                    {selectedProposal?.proposal_id === proposal.proposal_id && (
                      <div className="mt-4 border border-zinc-200 dark:border-zinc-700 p-4 bg-zinc-50 dark:bg-zinc-800/50" data-testid={`proposal-detail-${proposal.proposal_id}`}>
                        {/* Project Info */}
                        <div className="grid sm:grid-cols-3 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-zinc-500">Categoría</p>
                            <p className="font-medium text-sm">{quotation.category}</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500">Clave</p>
                            <p className="font-medium text-sm font-mono">{quotation.key}</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500">Comisión Plataforma</p>
                            <p className="font-medium text-sm text-orange-600">3% (proveedor) / 0% (cliente)</p>
                          </div>
                        </div>
                        {quotation.delivery_location?.address && (
                          <div className="mb-4">
                            <p className="text-xs text-zinc-500">Ubicación de Entrega</p>
                            <p className="font-medium text-sm flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-zinc-400" />{quotation.delivery_location.address}</p>
                          </div>
                        )}
                        <Separator className="my-3" />
                        {/* Materials List WITHOUT individual prices - with proper labels */}
                        <p className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase mb-3">Materiales Cotizados</p>
                        {/* Table header */}
                        <div className="hidden sm:grid sm:grid-cols-12 gap-2 text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase pb-2 border-b border-zinc-200 dark:border-zinc-700 mb-1">
                          <div className="col-span-5">Descripción del Material</div>
                          <div className="col-span-3">Marca</div>
                          <div className="col-span-3">Tiempo de Entrega</div>
                          <div className="col-span-1 text-center">Estado</div>
                        </div>
                        <div className="space-y-0">
                          {proposal.items?.filter(item => item.available).map((item, i) => (
                            <div key={i} className="sm:grid sm:grid-cols-12 gap-2 items-center text-sm py-2.5 border-b border-zinc-100 dark:border-zinc-700 last:border-0">
                              <div className="col-span-5 flex items-center gap-2">
                                <Package className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
                                <span className="text-zinc-800 dark:text-zinc-200 font-medium">{item.material_name}</span>
                              </div>
                              <div className="col-span-3 mt-1 sm:mt-0">
                                <span className="sm:hidden text-xs text-zinc-400 mr-1">Marca:</span>
                                {item.brand ? (
                                  <span className="text-xs bg-zinc-100 dark:bg-zinc-700 px-2 py-0.5 text-zinc-700 dark:text-zinc-300">{item.brand}</span>
                                ) : (
                                  <span className="text-xs text-zinc-400">—</span>
                                )}
                              </div>
                              <div className="col-span-3 mt-1 sm:mt-0">
                                <span className="sm:hidden text-xs text-zinc-400 mr-1">Entrega:</span>
                                {item.delivery_time ? (
                                  <span className="text-xs text-zinc-600 dark:text-zinc-400">
                                    <Clock className="w-3 h-3 inline mr-1" />
                                    {item.delivery_time} {!/día|dia|sem|hour|hora/i.test(item.delivery_time) ? 'días háb.' : ''}
                                  </span>
                                ) : (
                                  <span className="text-xs text-zinc-400">—</span>
                                )}
                              </div>
                              <div className="col-span-1 text-center mt-1 sm:mt-0">
                                <CheckCircle className="w-4 h-4 text-green-500 inline" />
                              </div>
                            </div>
                          ))}
                          {proposal.items?.filter(item => !item.available).length > 0 && (
                            <p className="text-xs text-red-500 mt-2">
                              {proposal.items.filter(item => !item.available).length} material(es) no disponible(s)
                            </p>
                          )}
                        </div>
                        {/* Rating info */}
                        <div className="mt-4 flex items-center gap-2 text-sm">
                          <Star className="w-4 h-4 text-amber-500" weight="fill" />
                          <span className="text-zinc-700 dark:text-zinc-300">Calificación del proveedor: <strong>{proposal.provider_rating?.toFixed(1) || '5.0'}</strong> ({proposal.provider_rating_count || 0} reseñas)</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="border-zinc-200 dark:border-zinc-700 rounded-none">
            <CardHeader className="border-b border-zinc-200 dark:border-zinc-700"><CardTitle className="text-base">Información</CardTitle></CardHeader>
            <CardContent className="p-4 space-y-4">
              <div><p className="text-sm text-zinc-500">Estado</p><Badge variant="outline" className="rounded-none mt-1" data-testid="quotation-status-badge">{({ draft: 'Borrador', sent: 'Enviada', in_progress: 'En Progreso', completed: 'Completada', accepted: 'Aceptada', cancelled: 'Cancelada', expired: 'Vencida' })[quotation.status] || quotation.status}</Badge></div>
              <div><p className="text-sm text-zinc-500">Fecha Límite</p><p className="font-medium">{new Date(quotation.deadline).toLocaleDateString('es-MX')}</p></div>
              {quotation.delivery_location?.address && (
                <div><p className="text-sm text-zinc-500 mb-2">Ubicación de Entrega</p>
                  <DeliveryMap
                    latitude={quotation.delivery_location.latitude || 19.4326}
                    longitude={quotation.delivery_location.longitude || -99.1332}
                    radius={quotation.delivery_location.radius || 5}
                    address={quotation.delivery_location.address}
                    readOnly={true}
                  />
                  {providerDistance !== null && (
                    <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800" data-testid="provider-distance">
                      <MapPin className="w-4 h-4 text-orange-500 flex-shrink-0" />
                      <span className="text-sm text-orange-700 dark:text-orange-300">
                        <strong>{providerDistance} km</strong> desde tu ubicación
                      </span>
                    </div>
                  )}
                </div>
              )}
              {quotation.project_name && (
                <div><p className="text-sm text-zinc-500">Proyecto</p><p className="font-medium">{quotation.project_name}</p></div>
              )}
            </CardContent>
          </Card>

          {/* Payment Summary Sidebar */}
          {ps && ps.has_winner && (
            <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="payment-summary">
              <CardHeader className="border-b border-zinc-200 dark:border-zinc-700"><CardTitle className="text-base">Resumen de Pago</CardTitle></CardHeader>
              <CardContent className="p-4 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-zinc-500">Total Propuesta</span><span className="font-bold">${ps.proposal_total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span></div>
                <Separator />
                <div className="flex justify-between"><span className="text-zinc-500">Comisión Cliente</span><span className="font-medium text-green-600">$0.00 (Gratis)</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Comisión Proveedor (3%)</span>
                  <span className="font-medium">${ps.provider_commission_amount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span>
                </div>
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center gap-2">{ps.client_confirmed ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Clock className="w-4 h-4 text-zinc-400" />}<span>Cliente confirmó</span></div>
                  <div className="flex items-center gap-2">{ps.provider_accepted ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Clock className="w-4 h-4 text-zinc-400" />}<span>Proveedor aceptó</span></div>
                  <div className="flex items-center gap-2">{ps.provider_paid ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Clock className="w-4 h-4 text-zinc-400" />}<span>Comisión pagada</span></div>
                  <div className="flex items-center gap-2">{ps.chat_available ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Clock className="w-4 h-4 text-zinc-400" />}<span>Chat disponible</span></div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Confirm Purchase Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Confirmar Compra</DialogTitle>
            <DialogDescription>
              Al confirmar, se notificará al proveedor ganador para que acepte la venta y pague su comisión del 3%.
              <br /><br /><strong>Esta acción es sin costo para ti (0% comisión).</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)} className="rounded-none">Cancelar</Button>
            <Button onClick={handleConfirmPurchase} disabled={confirmingPurchase} className="btn-primary rounded-none" data-testid="confirm-purchase-dialog-btn">
              {confirmingPurchase ? <CircleNotch className="w-5 h-5 animate-spin" /> : <><ShieldCheck className="w-5 h-5 mr-2" />Confirmar Compra</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MatchitoCelebration show={showMatchito} message={matchitoMsg} onClose={() => setShowMatchito(false)} />
    </div>
  );
};

export default QuotationDetailPage;
